import { legacyAirportRows } from './airports.generated';
import type { Airport } from '../game/types';

const TOURISM = new Set(['MFM', 'SYX', 'CJU', 'CTS', 'OKA', 'CEB', 'CNX', 'HKT', 'DPS', 'MLE', 'PPT', 'HNL', 'LAS', 'MIA', 'BCN', 'ATH', 'CPT', 'NAN']);
const BUSINESS = new Set(['HKG', 'SZX', 'CAN', 'PVG', 'PEK', 'TPE', 'ICN', 'HND', 'SIN', 'DEL', 'BOM', 'BLR', 'DXB', 'LHR', 'CDG', 'AMS', 'FRA', 'ZRH', 'JFK', 'SFO', 'LAX', 'ORD', 'YYZ']);
const PREMIUM_FEES = new Set(['HKG', 'HND', 'NRT', 'SIN', 'DXB', 'LHR', 'CDG', 'FRA', 'JFK', 'SFO', 'LAX', 'SYD']);
const LARGE_RUNWAY_OVERRIDE = new Set(['PPT', 'HNL', 'ANC', 'NAN', 'GUM', 'MLE', 'CMB', 'AKL']);

const COUNTRY_BY_IATA: Record<string, string> = {
  ICN: '韩国', PUS: '韩国', CJU: '韩国',
  HND: '日本', NRT: '日本', KIX: '日本', NGO: '日本', FUK: '日本', CTS: '日本', OKA: '日本',
  MNL: '菲律宾', CEB: '菲律宾',
  SIN: '新加坡',
  KUL: '马来西亚', PEN: '马来西亚', BKI: '马来西亚',
  BWN: '文莱',
  BKK: '泰国', CNX: '泰国', HKT: '泰国',
  SGN: '越南', HAN: '越南', DAD: '越南',
  PNH: '柬埔寨', REP: '柬埔寨',
  VTE: '老挝',
  RGN: '缅甸',
  CGK: '印度尼西亚', DPS: '印度尼西亚', SUB: '印度尼西亚',
  DAC: '孟加拉国',
  CMB: '斯里兰卡',
  MLE: '马尔代夫',
  KTM: '尼泊尔',
  DEL: '印度', BOM: '印度', BLR: '印度', MAA: '印度', HYD: '印度', CCU: '印度',
  DXB: '阿联酋', AUH: '阿联酋',
  DOH: '卡塔尔',
  RUH: '沙特阿拉伯', JED: '沙特阿拉伯',
  TLV: '以色列',
  IST: '土耳其',
  SVO: '俄罗斯',
  LHR: '英国', MAN: '英国',
  DUB: '爱尔兰',
  CDG: '法国',
  AMS: '荷兰',
  FRA: '德国',
  ZRH: '瑞士',
  MAD: '西班牙', BCN: '西班牙',
  FCO: '意大利', MXP: '意大利',
  VIE: '奥地利',
  WAW: '波兰',
  PRG: '捷克',
  ATH: '希腊',
  CPH: '丹麦',
  OSL: '挪威',
  ARN: '瑞典',
  HEL: '芬兰',
  CAI: '埃及',
  JNB: '南非', CPT: '南非',
  NBO: '肯尼亚',
  ADD: '埃塞俄比亚',
  LOS: '尼日利亚',
  SYD: '澳大利亚', MEL: '澳大利亚', BNE: '澳大利亚', PER: '澳大利亚',
  AKL: '新西兰',
  NAN: '斐济',
  PPT: '法属波利尼西亚',
  GUM: '美国',
  HNL: '美国', ANC: '美国',
  YVR: '加拿大', YYZ: '加拿大', YUL: '加拿大',
  SEA: '美国', SFO: '美国', LAX: '美国', SAN: '美国', LAS: '美国', PHX: '美国',
  DFW: '美国', IAH: '美国', ORD: '美国', ATL: '美国', MIA: '美国', BOS: '美国',
  JFK: '美国', EWR: '美国', IAD: '美国',
  MEX: '墨西哥',
  GRU: '巴西', GIG: '巴西',
  EZE: '阿根廷',
  SCL: '智利',
  LIM: '秘鲁',
  BOG: '哥伦比亚',
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

function cabinProfileFor(market: number, business: number, tourism: number, international: number): import('../game/types').CabinProfile {
  const tourismFactor = tourism / 10;
  const businessShare = clamp(0.08 + business * 0.012 + international * 0.006 - tourismFactor * 0.03, 0.04, 0.35);
  const firstShare = clamp(0.005 + business * 0.004 + international * 0.003, 0, 0.12);
  const economyShare = clamp(1 - businessShare - firstShare, 0.5, 1);
  const total = economyShare + businessShare + firstShare;
  return {
    economy: economyShare / total,
    business: businessShare / total,
    first: firstShare / total,
  };
}

function regionFor(lat: number, lon: number): string {
  if (lon >= 95 && lon <= 150 && lat >= 15) return '东亚';
  if (lon >= 90 && lon <= 145 && lat < 15 && lat > -15) return '东南亚';
  if (lon >= 65 && lon < 95 && lat > 5) return '南亚';
  if (lon >= 30 && lon < 65 && lat > 10) return '中东';
  if (lon >= -15 && lon < 40 && lat > 34) return '欧洲';
  if (lon >= -20 && lon < 55 && lat <= 34) return '非洲';
  if (lon > 110 && lat <= -15) return '大洋洲';
  if (lon < -30 && lat >= 10) return '北美';
  if (lon < -30 && lat < 10) return '南美';
  return '跨洋地区';
}

export const AIRPORTS: Airport[] = legacyAirportRows.map((row) => {
  const [iata, nameZh, nameEn, lat, lon, market] = row;
  const tourism = clamp(market + (TOURISM.has(iata) ? 3 : -1), 1, 10);
  const business = clamp(market + (BUSINESS.has(iata) ? 2 : -1), 1, 10);
  const international = clamp(Math.round(market * 0.85 + (BUSINESS.has(iata) ? 2 : 0)), 1, 10);
  const baseRunway = market >= 8 ? 4 : market >= 5 ? 3 : market >= 3 ? 2 : 1;

  return {
    iata,
    nameZh,
    nameEn,
    lat,
    lon,
    market,
    business,
    tourism,
    international,
    feeIndex: 0.48 + market * 0.11 + (PREMIUM_FEES.has(iata) ? 0.35 : 0),
    runwayClass: (LARGE_RUNWAY_OVERRIDE.has(iata) ? Math.max(3, baseRunway) : baseRunway) as 1 | 2 | 3 | 4,
    weeklyMovements: 110 + market * 46,
    reliability: clamp(0.96 - market * 0.007 - (['HKG', 'HND', 'PEK', 'LHR'].includes(iata) ? 0.025 : 0), 0.82, 0.97),
    region: regionFor(lat, lon),
    country: COUNTRY_BY_IATA[iata] ?? '中国',
    cabinProfile: cabinProfileFor(market, business, tourism, international),
  };
});

export const AIRPORT_BY_ID = new Map(AIRPORTS.map((airport) => [airport.iata, airport]));

export function airportById(iata: string): Airport {
  const airport = AIRPORT_BY_ID.get(iata);
  if (!airport) throw new Error(`未知机场：${iata}`);
  return airport;
}
